/**
 * ContentStandardManager
 *
 * Available ONLY to tenant_admin users (rendered inside TenantAdminDashboard).
 * Provides:
 *   1. List all active content standards (global + tenant-specific)
 *   2. Inspect any standard (opens shared ContentStandardInspectModal)
 *   3. Clone a standard → open the full Clone & Customise form
 *   4. Save the customised clone as a tenant-specific standard
 *
 * The Clone form enforces:
 *   • Unit structure weights must sum to exactly 100%
 *   • All required fields present
 *   • Word budget: min < target < max
 *   • At least one section in unit_structure
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Table, Button, Badge, Modal, Form, Alert, Spinner,
    Row, Col, InputGroup, ProgressBar, Nav, Accordion
} from 'react-bootstrap';
import { Plus, Trash2, GripVertical, Info } from 'lucide-react';
import {
    getContentStandards,
    getContentStandardDetail,
    createContentStandard,
} from '../../api/api';
import ContentStandardInspectModal from '../Common/ContentStandardInspectModal';
import { showToast } from '../../utils/toast';

// ── constants ─────────────────────────────────────────────────────────────────

const LANGUAGE_VARIANTS = [
    { value: 'UK_ENGLISH',       label: 'UK English' },
    { value: 'US_ENGLISH',       label: 'US English' },
    { value: 'INDIAN_ENGLISH',   label: 'Indian English' },
    { value: 'EUROPEAN_ENGLISH', label: 'European English' },
];

const QUESTION_TYPES = ['mcq', 'short_note', 'essay', 'case_study', 'true_false', 'short_answer'];

const FIELD_HELP = {
    name:            'A unique, descriptive name for this content standard. Shown in the dropdown during orchestration.',
    description:     'Explain the intended audience, discipline, or institutional context where this standard applies.',
    pedagogical_model: 'The instructional design framework. ARCS (Attention, Relevance, Confidence, Satisfaction) is the default.',
    language_variant:  'Controls spelling, phrasing, and vocabulary conventions used by the AI.',
    word_count_target: 'Total word budget per unit. This is distributed across sections according to the weights below.',
    unit_structure:  'Ordered list of content sections. Each section\'s weight controls what fraction of the total word budget it receives. All weights must sum to 100%.',
    assessment_mix:  'Number of questions of each type to generate per blueprint run. Leave a type at 0 to exclude it.',
    word_budget:     'Fine-grained word budget control. The AI targets the "target" count and clamps between min and max.',
};

const SECTION_HINTS = {
    learning_objectives:    'States what students will achieve — usually 3-5 outcome verbs.',
    introduction:           'Frames the topic\'s relevance and academic context. Avoids defining concepts covered later.',
    concept_explanation:    'Core content: definitions, theory, derivations, and foundational understanding.',
    examples:               'Worked examples grounded in the concept. Should be concrete and course-specific.',
    real_world_application: 'Industry or professional scenarios that apply the concept in practice.',
    check_your_progress:    'Formative self-assessment questions (not graded). Helps students gauge understanding.',
    summary:                'Concise bullet-point recap of the key ideas in this unit.',
    conclusion:             'Keywords, closing thoughts, or cross-references to related units.',
    activity_or_case:       'Optional case study, design activity, or problem set.',
    consolidate:            'Synthesis section that integrates all concepts of the unit.',
};

const humanise = s => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const pct = w => `${(w * 100).toFixed(1)}%`;

// ── Clone Form ────────────────────────────────────────────────────────────────

function CloneForm({ source, tenantSlug, onSaved, onCancel }) {
    // Initialise from source
    const initSections = () => {
        const me = source?.mandatory_elements || {};
        const us = Array.isArray(me.unit_structure) ? me.unit_structure : [];
        return us.map(s => ({ name: s.name || '', weight: +(s.weight || 0.1).toFixed(4) }));
    };

    const initBudget = () => {
        const lc = source?.locale_config || {};
        const st = lc.structure || {};
        const uw = st.unit_word_count || {};
        const ds = st.dynamic_word_scaling || {};
        return {
            min: uw.min ?? 3500,
            max: uw.max ?? 5000,
            target: uw.target ?? (source?.word_count_target || 4500),
            dynamicEnabled: !!ds.enabled,
            baseWords: ds.base_words_per_topic ?? 750,
        };
    };

    const initMix = () => {
        const mix = source?.assessment_mix || {};
        return QUESTION_TYPES.reduce((acc, t) => ({ ...acc, [t]: mix[t] ?? 0 }), {});
    };

    const [activeTab,  setActiveTab]  = useState('basic');
    const [name,       setName]       = useState(`${source?.name || ''} (Custom)`);
    const [description, setDesc]      = useState(source?.description || '');
    const [model,      setModel]      = useState(source?.pedagogical_model || 'ARCS');
    const [langVar,    setLangVar]    = useState(source?.language_variant || 'UK_ENGLISH');
    const [wordTarget, setWordTarget] = useState(source?.word_count_target || 4500);
    const [sections,   setSections]   = useState(initSections);
    const [budget,     setBudget]     = useState(initBudget);
    const [mix,        setMix]        = useState(initMix);
    const [saving,     setSaving]     = useState(false);
    const [errors,     setErrors]     = useState({});

    // Live validation
    const weightSum = sections.reduce((a, s) => a + (s.weight || 0), 0);
    const weightOk  = Math.abs(weightSum - 1.0) < 0.001;

    const validate = useCallback(() => {
        const e = {};
        if (!name.trim()) e.name = 'Name is required.';
        if (!wordTarget || wordTarget < 500) e.wordTarget = 'Word count must be at least 500.';
        if (sections.length === 0) e.sections = 'At least one section is required.';
        sections.forEach((s, i) => {
            if (!s.name.trim()) e[`sec_name_${i}`] = `Section ${i + 1} needs a name.`;
            if (s.weight <= 0) e[`sec_weight_${i}`] = `Section ${i + 1} weight must be > 0.`;
        });
        if (!weightOk) e.weightSum = `Weights must sum to 100%. Current total: ${pct(weightSum)}.`;
        if (budget.min >= budget.max) e.budgetRange = 'Min must be less than Max.';
        if (budget.target < budget.min || budget.target > budget.max)
            e.budgetTarget = 'Target must be between Min and Max.';
        return e;
    }, [name, wordTarget, sections, weightOk, weightSum, budget]);

    const handleSave = async () => {
        const e = validate();
        setErrors(e);
        if (Object.keys(e).length > 0) return;

        const payload = {
            name: name.trim(),
            description: description.trim(),
            word_count_target: Number(wordTarget),
            language_variant: langVar,
            pedagogical_model: model,
            mandatory_elements: {
                unit_structure: sections.map(s => ({ name: s.name.trim(), weight: s.weight })),
            },
            assessment_mix: Object.fromEntries(
                Object.entries(mix).filter(([, v]) => v > 0).map(([k, v]) => [k, Number(v)])
            ),
            locale_config: {
                structure: {
                    unit_word_count: { min: budget.min, max: budget.max, target: budget.target },
                    dynamic_word_scaling: {
                        enabled: budget.dynamicEnabled,
                        base_words_per_topic: budget.baseWords,
                    },
                },
            },
        };

        try {
            setSaving(true);
            const res = await createContentStandard(tenantSlug, payload);
            showToast(`Content standard "${res.data.name}" created successfully.`, 'success');
            onSaved(res.data);
        } catch (err) {
            showToast(err?.response?.data?.detail || 'Failed to create content standard.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Section helpers
    const addSection = () => setSections(p => [...p, { name: '', weight: 0.05 }]);
    const removeSection = i => setSections(p => p.filter((_, idx) => idx !== i));
    const updateSection = (i, key, val) =>
        setSections(p => p.map((s, idx) => idx === i ? { ...s, [key]: val } : s));

    const autoBalanceWeights = () => {
        const n = sections.length;
        if (n === 0) return;
        const even = +(1 / n).toFixed(4);
        setSections(p => p.map((s, i) =>
            i < n - 1 ? { ...s, weight: even } : { ...s, weight: +(1 - even * (n - 1)).toFixed(4) }
        ));
    };

    const TABS = [
        { key: 'basic',      label: '① Basic Info' },
        { key: 'structure',  label: '② Unit Sections' },
        { key: 'budget',     label: '③ Word Budget' },
        { key: 'assessment', label: '④ Assessment Mix' },
    ];

    return (
        <div>
            {/* Tab nav */}
            <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab} className="mb-4">
                {TABS.map(t => (
                    <Nav.Item key={t.key}>
                        <Nav.Link eventKey={t.key} style={{ fontSize: 13 }}>{t.label}</Nav.Link>
                    </Nav.Item>
                ))}
            </Nav>

            {/* ── Basic Info ── */}
            {activeTab === 'basic' && (
                <div>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">
                            Standard Name <span className="text-danger" aria-hidden="true">*</span>
                        </Form.Label>
                        <Form.Control
                            value={name}
                            onChange={e => setName(e.target.value)}
                            isInvalid={!!errors.name}
                            placeholder="e.g., ARCS – Medical Sciences (Tamil Nadu)"
                        />
                        <Form.Text className="text-muted">{FIELD_HELP.name}</Form.Text>
                        <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold small">Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={description}
                            onChange={e => setDesc(e.target.value)}
                            placeholder="Describe the intended use, discipline, or institutional context."
                        />
                        <Form.Text className="text-muted">{FIELD_HELP.description}</Form.Text>
                    </Form.Group>

                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold small">
                                    Pedagogical Model <span className="text-danger" aria-hidden="true">*</span>
                                </Form.Label>
                                <Form.Control
                                    value={model}
                                    onChange={e => setModel(e.target.value)}
                                    placeholder="ARCS"
                                />
                                <Form.Text className="text-muted">{FIELD_HELP.pedagogical_model}</Form.Text>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold small">
                                    Language Variant <span className="text-danger" aria-hidden="true">*</span>
                                </Form.Label>
                                <Form.Select value={langVar} onChange={e => setLangVar(e.target.value)}>
                                    {LANGUAGE_VARIANTS.map(lv => (
                                        <option key={lv.value} value={lv.value}>{lv.label}</option>
                                    ))}
                                </Form.Select>
                                <Form.Text className="text-muted">{FIELD_HELP.language_variant}</Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mt-3">
                        <Form.Label className="fw-semibold small">
                            Total Word Count Target (per unit) <span className="text-danger" aria-hidden="true">*</span>
                        </Form.Label>
                        <InputGroup>
                            <Form.Control
                                type="number"
                                min={500} max={20000} step={100}
                                value={wordTarget}
                                onChange={e => setWordTarget(Number(e.target.value))}
                                isInvalid={!!errors.wordTarget}
                            />
                            <InputGroup.Text>words</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">{FIELD_HELP.word_count_target}</Form.Text>
                        <Form.Control.Feedback type="invalid">{errors.wordTarget}</Form.Control.Feedback>
                    </Form.Group>
                </div>
            )}

            {/* ── Unit Sections ── */}
            {activeTab === 'structure' && (
                <div>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <p className="text-muted small mb-0">
                            Define the ordered sections for each AI-generated unit.
                            <strong> Weights must sum to exactly 100%.</strong>
                            <br />Each section receives <code>weight × {wordTarget.toLocaleString()}</code> words.
                        </p>
                        <div className="d-flex gap-2 ms-3 flex-shrink-0">
                            <Button variant="outline-secondary" size="sm" onClick={autoBalanceWeights} title="Distribute weights evenly">
                                ⚖ Balance
                            </Button>
                            <Button variant="outline-primary" size="sm" onClick={addSection}>
                                <Plus size={14} /> Add Section
                            </Button>
                        </div>
                    </div>

                    {/* Weight sum indicator */}
                    <div className="mb-3">
                        <div className="d-flex justify-content-between small mb-1">
                            <span className="text-muted">Total weight</span>
                            <span className={weightOk ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>
                                {pct(weightSum)} {weightOk ? '✔ Valid' : `— needs ${pct(1 - weightSum)} more`}
                            </span>
                        </div>
                        <ProgressBar
                            now={Math.min(weightSum * 100, 100)}
                            variant={weightOk ? 'success' : weightSum > 1 ? 'danger' : 'warning'}
                            style={{ height: 6 }}
                            aria-label={`Total weight ${pct(weightSum)}`}
                        />
                        {errors.weightSum && <div className="text-danger small mt-1">{errors.weightSum}</div>}
                        {errors.sections && <div className="text-danger small mt-1">{errors.sections}</div>}
                    </div>

                    {sections.map((s, i) => (
                        <div key={i} className="border rounded p-3 mb-2 bg-light">
                            <Row className="g-2 align-items-start">
                                <Col xs={1} className="text-center pt-2 text-muted">
                                    <span className="fw-bold small">{i + 1}</span>
                                </Col>
                                <Col md={5}>
                                    <Form.Control
                                        size="sm"
                                        placeholder="section_name (snake_case)"
                                        value={s.name}
                                        onChange={e => updateSection(i, 'name', e.target.value)}
                                        isInvalid={!!errors[`sec_name_${i}`]}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors[`sec_name_${i}`]}</Form.Control.Feedback>
                                    <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                                        {SECTION_HINTS[s.name] || 'Description of this section\'s purpose'}
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <InputGroup size="sm">
                                        <Form.Control
                                            type="number"
                                            min={0} max={1} step={0.01}
                                            value={(s.weight * 100).toFixed(1)}
                                            onChange={e => updateSection(i, 'weight', +(Number(e.target.value) / 100).toFixed(4))}
                                            isInvalid={!!errors[`sec_weight_${i}`]}
                                        />
                                        <InputGroup.Text>%</InputGroup.Text>
                                    </InputGroup>
                                    <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                                        ≈ {Math.round(wordTarget * s.weight).toLocaleString()} words
                                    </div>
                                </Col>
                                <Col xs={1}>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => removeSection(i)}
                                        aria-label={`Remove section ${i + 1}`}
                                        disabled={sections.length <= 1}
                                    >
                                        <Trash2 size={14} aria-hidden="true" />
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Word Budget ── */}
            {activeTab === 'budget' && (
                <div>
                    <p className="text-muted small mb-4">{FIELD_HELP.word_budget}</p>

                    <Row className="g-3 mb-4">
                        {[
                            { key: 'min', label: 'Minimum words', help: 'Shortest acceptable unit length.' },
                            { key: 'target', label: 'Target words', help: 'The AI aims for this count.' },
                            { key: 'max', label: 'Maximum words', help: 'Hard ceiling — AI will not exceed this.' },
                        ].map(({ key, label, help }) => (
                            <Col md={4} key={key}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold small">{label}</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="number" min={100} step={50}
                                            value={budget[key]}
                                            onChange={e => setBudget(p => ({ ...p, [key]: Number(e.target.value) }))}
                                            isInvalid={!!(errors.budgetRange || errors.budgetTarget)}
                                        />
                                        <InputGroup.Text>words</InputGroup.Text>
                                    </InputGroup>
                                    <Form.Text className="text-muted">{help}</Form.Text>
                                </Form.Group>
                            </Col>
                        ))}
                    </Row>
                    {(errors.budgetRange || errors.budgetTarget) && (
                        <Alert variant="danger" role="alert" className="small">
                            {errors.budgetRange || errors.budgetTarget}
                        </Alert>
                    )}

                    <div className="border rounded p-3 bg-light">
                        <Form.Check
                            type="switch"
                            id="dynamic-scaling"
                            label={<><strong>Dynamic word scaling</strong><span className="text-muted small ms-2">— scale unit length based on number of topics</span></>}
                            checked={budget.dynamicEnabled}
                            onChange={e => setBudget(p => ({ ...p, dynamicEnabled: e.target.checked }))}
                        />
                        {budget.dynamicEnabled && (
                            <Form.Group className="mt-3 ms-4" style={{ maxWidth: 280 }}>
                                <Form.Label className="small fw-semibold">Base words per topic</Form.Label>
                                <InputGroup size="sm">
                                    <Form.Control
                                        type="number" min={100} step={50}
                                        value={budget.baseWords}
                                        onChange={e => setBudget(p => ({ ...p, baseWords: Number(e.target.value) }))}
                                    />
                                    <InputGroup.Text>words/topic</InputGroup.Text>
                                </InputGroup>
                                <Form.Text className="text-muted">
                                    Unit word count = base × topic count, clamped between min and max.
                                </Form.Text>
                            </Form.Group>
                        )}
                    </div>
                </div>
            )}

            {/* ── Assessment Mix ── */}
            {activeTab === 'assessment' && (
                <div>
                    <p className="text-muted small mb-4">{FIELD_HELP.assessment_mix}</p>
                    <Table size="sm" bordered style={{ fontSize: 13 }}>
                        <thead className="table-light">
                            <tr>
                                <th scope="col">Question Type</th>
                                <th scope="col" style={{ width: 160 }}>Count per run</th>
                                <th scope="col">Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {QUESTION_TYPES.map(type => (
                                <tr key={type}>
                                    <td><Badge bg="secondary">{humanise(type)}</Badge></td>
                                    <td>
                                        <InputGroup size="sm">
                                            <Form.Control
                                                type="number" min={0} max={50}
                                                value={mix[type] || 0}
                                                onChange={e => setMix(p => ({ ...p, [type]: Number(e.target.value) }))}
                                            />
                                        </InputGroup>
                                    </td>
                                    <td className="text-muted small">
                                        {type === 'mcq'          ? 'Auto-graded, 4 options, answer key' :
                                         type === 'short_note'   ? 'Short written response, 2-5 sentences' :
                                         type === 'essay'        ? 'Long-form response with rubric' :
                                         type === 'case_study'   ? 'Scenario-based applied question' :
                                         type === 'true_false'   ? 'Binary choice question' :
                                         type === 'short_answer' ? 'One to two sentence factual answer' : ''}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            {/* Footer actions */}
            <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                <div>
                    {Object.keys(validate()).length > 0 && activeTab !== 'structure' && (
                        <span className="text-danger small">
                            There are validation errors. Please review all tabs.
                        </span>
                    )}
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSave}
                        disabled={saving || !weightOk}
                        title={!weightOk ? 'Weights must sum to 100% before saving' : ''}
                    >
                        {saving ? <><Spinner size="sm" aria-hidden="true" /> Saving…</> : '✓ Save Custom Standard'}
                    </Button>
                </div>
            </div>
        </div>
    );
}


// ── ContentStandardManager ────────────────────────────────────────────────────

export default function ContentStandardManager({ tenantSlug, onStandardCreated }) {
    const [standards,       setStandards]       = useState([]);
    const [loading,         setLoading]         = useState(true);
    const [inspectId,       setInspectId]       = useState(null);
    const [inspectOpen,     setInspectOpen]     = useState(false);
    const [cloneSource,     setCloneSource]     = useState(null);
    const [showClone,       setShowClone]       = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getContentStandards(tenantSlug);
            setStandards(res.data || []);
        } catch {
            showToast('Failed to load content standards.', 'error');
        } finally {
            setLoading(false);
        }
    }, [tenantSlug]);

    useEffect(() => { if (tenantSlug) load(); }, [load, tenantSlug]);

    const handleInspect = (id) => { setInspectId(id); setInspectOpen(true); };

    const handleClone = async (baseData) => {
        // If we already have full data (from inspect modal), use it directly
        if (baseData.mandatory_elements !== undefined) {
            setCloneSource(baseData);
            setShowClone(true);
            return;
        }
        // Otherwise fetch detail first
        try {
            const res = await getContentStandardDetail(tenantSlug, baseData.id || baseData);
            setCloneSource(res.data);
            setShowClone(true);
        } catch {
            showToast('Failed to load standard details for cloning.', 'error');
        }
    };

    const handleSaved = (newStd) => {
        setStandards(prev => [...prev, newStd]);
        setShowClone(false);
        setCloneSource(null);
        onStandardCreated?.(newStd);
    };

    return (
        <>
            <Card className="mb-4 shadow-sm border-0 rounded-4">
                <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
                    <div>
                        <h6 className="mb-0 fw-bold text-primary text-uppercase">Content Standards</h6>
                        <small className="text-muted">
                            Global standards are read-only. Clone one to create a tenant-specific customisation.
                        </small>
                    </div>
                    <Badge bg="light" text="dark" className="border">{standards.length} standards</Badge>
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner size="sm" aria-hidden="true" />
                            <span className="sr-only">Loading content standards…</span>
                        </div>
                    ) : (
                        <Table hover size="sm" className="mb-0" style={{ fontSize: 13 }}>
                            <thead className="table-light">
                                <tr>
                                    <th scope="col">Name</th>
                                    <th scope="col">Model</th>
                                    <th scope="col" className="text-center">Word Target</th>
                                    <th scope="col">Scope</th>
                                    <th scope="col" className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {standards.map(s => (
                                    <tr key={s.id}>
                                        <td className="fw-medium">{s.name}</td>
                                        <td><Badge bg="light" text="dark" className="border">{s.pedagogical_model || 'ARCS'}</Badge></td>
                                        <td className="text-center text-muted">
                                            {(s.word_count_target || 4500).toLocaleString()} words
                                        </td>
                                        <td>
                                            {s.tenant_id
                                                ? <Badge bg="warning" text="dark">Tenant Custom</Badge>
                                                : <Badge bg="secondary">Global</Badge>}
                                        </td>
                                        <td className="text-end">
                                            <div className="d-flex gap-1 justify-content-end">
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => handleInspect(s.id)}
                                                    aria-label={`Inspect ${s.name}`}
                                                >
                                                    <Info size={13} aria-hidden="true" /> Inspect
                                                </Button>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => handleClone(s)}
                                                    aria-label={`Clone ${s.name}`}
                                                >
                                                    Clone &amp; Edit
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Shared inspect modal */}
            <ContentStandardInspectModal
                show={inspectOpen}
                onHide={() => setInspectOpen(false)}
                standardId={inspectId}
                slug={tenantSlug}
                onClone={handleClone}
            />

            {/* Clone & Edit modal — tenant_admin only */}
            <Modal
                show={showClone}
                onHide={() => setShowClone(false)}
                size="xl"
                centered
                aria-labelledby="clone-cs-title"
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="clone-cs-title" style={{ fontSize: 16 }}>
                        Clone &amp; Customise Content Standard
                        {cloneSource && (
                            <span className="text-muted fw-normal ms-2" style={{ fontSize: 13 }}>
                                — based on "{cloneSource.name}"
                            </span>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 pt-4 pb-2" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                    {cloneSource && (
                        <CloneForm
                            source={cloneSource}
                            tenantSlug={tenantSlug}
                            onSaved={handleSaved}
                            onCancel={() => setShowClone(false)}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
}
